import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {RootState} from "../store";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import {logout, setToken, toToken} from "../features/authSlice";
import {
  API_PROXY_CLIENT_LOGIN_ENDPOINT,
  API_PROXY_LOGOUT_ENDPOINT,
  API_PROXY_REFRESHTOKEN_ENDPOINT,
  API_TOKEN_ENDPOINT,
} from "../../constants/endpoints";
import {Mutex} from "async-mutex";

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === "true";
const describeArgs = (args: string | FetchArgs) =>
  typeof args === "string"
    ? args
    : {
        url: args.url,
        method: args.method ?? "GET",
        hasBody: Boolean(args.body),
      };

const mutex = new Mutex();
const NON_REFRESH_ENDPOINTS = [
  API_TOKEN_ENDPOINT,
  API_PROXY_CLIENT_LOGIN_ENDPOINT,
  API_PROXY_REFRESHTOKEN_ENDPOINT,
];

export const isNonRefreshEndpoint = (url: string) =>
  NON_REFRESH_ENDPOINTS.some(endpoint => url.includes(endpoint));

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_IDMS_API_URL,
  // Rely on the default "same-origin" credentials mode. The reverse proxy makes /api calls
  // same-origin, so the httpOnly refresh cookie is sent automatically — without opting into
  // cross-origin credentialed CORS, which would otherwise require every cross-origin caller
  // to be explicitly allow-listed (and break those that aren't).
  prepareHeaders: (headers, {getState, endpoint}) => {
    const state = getState() as RootState;
    const accessToken = state.auth?.token?.accessToken;

    if (accessToken && endpoint !== "login") {
      headers.set("Content-Type", "application/json");
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  if (DEBUG_AUTH) {
    console.debug("[api] request", describeArgs(args));
  }
  let result = await baseQuery(args, api, extraOptions);
  if (DEBUG_AUTH) {
    console.debug("[api] response", {
      ok: !result.error,
      status: (result as any)?.meta?.response?.status,
      error: result.error && {
        status: (result.error as any)?.status,
        dataType: typeof (result.error as any)?.data,
      },
    });
  }

  if (result.error && result.error.status === 401) {
    // normalize args to get the requested URL
    const url = typeof args === "string" ? args : args.url;

    // Login endpoints should surface their own errors instead of entering the refresh loop.
    if (isNonRefreshEndpoint(url)) {
      return result;
    }

    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // Ask the API for a new access token. The refresh token travels in the
        // httpOnly cookie (sent automatically via credentials: "include"), and the
        // client id/secret are resolved server-side — nothing sensitive is sent from JS.
        if (DEBUG_AUTH) {
          console.debug("[api] attempting refresh", {
            url: API_PROXY_REFRESHTOKEN_ENDPOINT,
          });
        }
        const refreshResult = await baseQuery(
          {
            url: API_PROXY_REFRESHTOKEN_ENDPOINT,
            method: "POST",
          },
          api,
          extraOptions,
        );

        const loginResponse = refreshResult.data as LoginResponse | undefined;
        // Require a real access token — guards against a 2xx with an empty/unexpected body
        // (e.g. a misrouted proxy returning HTML) being treated as a successful refresh.
        if (loginResponse?.access_token) {
          // store the new access token (refresh token stays in the cookie, not in JS state)
          api.dispatch(setToken(toToken(loginResponse)));
          if (DEBUG_AUTH) {
            console.debug("[api] refresh success; retrying original request");
          }
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          if (DEBUG_AUTH) {
            console.debug("[api] refresh failed; clearing cookie, logging out");
          }
          // Clear the now-invalid refresh cookie server-side so it doesn't linger.
          await baseQuery(
            {url: API_PROXY_LOGOUT_ENDPOINT, method: "POST"},
            api,
            extraOptions,
          );
          api.dispatch(logout());
          // move to login page
          window.location.href = "/login";
        }
      } catch (e) {
        if (DEBUG_AUTH) {
          console.debug("[api] refresh error", e);
        } else {
          console.error(e);
        }
        // Clear the now-invalid refresh cookie server-side so it doesn't linger.
        await baseQuery(
          {url: API_PROXY_LOGOUT_ENDPOINT, method: "POST"},
          api,
          extraOptions,
        );
        api.dispatch(logout());
        // move to login page
        window.location.href = "/login";
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const idmsApi = createApi({
  reducerPath: "idmsApi",
  baseQuery: baseQueryWithReauth,
  // global configuration for the api
  // keepUnusedDataFor: 600,
  // Add domain-specific tags to support cache invalidation across pages
  tagTypes: [
    "Auth",
    "User",
    "UserRole",
    "RoleUser",
    "UserProduct",
    // Target Manager specific
    "Product",
    "ProductFeatures",
    "ProductSubscriptions",
    "SubscriptionFeatures",
    "AddOns",
    "ProductDealers",
  ],
  endpoints: () => ({}),
});
